import { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PdfViewer } from "./PdfViewer";
import { FullTextViewer } from "./FullTextViewer";
import apiClient from "@/lib/api-client";

interface Props {
  open: boolean;
  onClose: () => void;
  conceptId: string;
  paperId: string;
  paperTitle: string;
  excerptText: string;
  pageNumber?: number;
}

interface LoadedPaper {
  paperId: string;
  pdfUrl?: string;
  fullText?: string;
  error?: string;
}

/**
 * Keep-alive PDF viewer pool. Instead of mounting/unmounting PdfViewers
 * (which destroys pdf.js documents and causes stale cache crashes),
 * we keep loaded PdfViewers mounted but hidden. Switching between
 * already-loaded papers is instant.
 */
export function EvidenceSourceDrawer({
  open,
  onClose,
  conceptId,
  paperId,
  paperTitle,
  excerptText,
  pageNumber,
}: Props) {
  // Pool of loaded papers — keyed by paperId, persists across evidence switches
  const [loadedPapers, setLoadedPapers] = useState<Map<string, LoadedPaper>>(new Map());
  const [loadingPaperId, setLoadingPaperId] = useState<string | null>(null);

  // Track which papers are currently being fetched to avoid duplicate requests
  const fetchingRef = useRef<Set<string>>(new Set());

  const loadPaper = useCallback((pid: string) => {
    // Already loaded or currently fetching
    if (loadedPapers.has(pid) || fetchingRef.current.has(pid)) return;

    fetchingRef.current.add(pid);
    setLoadingPaperId(pid);

    apiClient
      .get(`api/concepts/${conceptId}/pdf/download-url/${pid}`)
      .json<{ download_url: string }>()
      .then((res) => {
        setLoadedPapers((prev) => {
          const next = new Map(prev);
          next.set(pid, { paperId: pid, pdfUrl: res.download_url });
          return next;
        });
        fetchingRef.current.delete(pid);
        setLoadingPaperId((cur) => (cur === pid ? null : cur));
      })
      .catch(() => {
        // Try full text
        apiClient
          .get(`api/concepts/${conceptId}/pdf/full-text/${pid}`)
          .json<{ text: string; title: string }>()
          .then((res) => {
            setLoadedPapers((prev) => {
              const next = new Map(prev);
              next.set(pid, { paperId: pid, fullText: res.text });
              return next;
            });
          })
          .catch(() => {
            setLoadedPapers((prev) => {
              const next = new Map(prev);
              next.set(pid, { paperId: pid, error: "No PDF or full text available" });
              return next;
            });
          })
          .finally(() => {
            fetchingRef.current.delete(pid);
            setLoadingPaperId((cur) => (cur === pid ? null : cur));
          });
      });
  }, [conceptId, loadedPapers]);

  // Load the current paper when it changes
  useEffect(() => {
    if (!open) return;
    loadPaper(paperId);
  }, [open, paperId, loadPaper]);

  if (!open) return null;

  const isLoading = loadingPaperId === paperId && !loadedPapers.has(paperId);
  const activePaper = loadedPapers.get(paperId);
  const normalizedExcerpt = excerptText.replace(/\s+/g, " ").trim();

  return (
    <div className="flex h-full w-1/2 min-w-[400px] shrink-0 flex-col border-l bg-background">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{paperTitle}</p>
          <p className="truncate text-xs text-muted-foreground">
            Showing: &ldquo;{excerptText.slice(0, 60)}&rdquo;
          </p>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      {/* Content — all loaded PdfViewers stay mounted, only active one visible */}
      <div className="flex-1 overflow-hidden relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground z-10 bg-background">
            Loading source...
          </div>
        )}

        {activePaper?.error && (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {activePaper.error}
          </div>
        )}

        {/* PDF pool — keep all loaded PdfViewers mounted, show/hide */}
        {Array.from(loadedPapers.values()).map((lp) => {
          if (!lp.pdfUrl) return null;
          const isActive = lp.paperId === paperId;
          return (
            <div
              key={lp.paperId}
              className="absolute inset-0"
              style={{ display: isActive ? "block" : "none" }}
            >
              <PdfViewer
                url={lp.pdfUrl}
                initialPage={isActive ? pageNumber : undefined}
                searchQuery={isActive ? normalizedExcerpt : undefined}
              />
            </div>
          );
        })}

        {/* Full text (no pool needed — lightweight) */}
        {activePaper?.fullText && (
          <FullTextViewer
            text={activePaper.fullText}
            highlightText={excerptText}
          />
        )}
      </div>
    </div>
  );
}
